import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Key,
  Building2,
  Calendar,
  CheckCircle2,
  ExternalLink,
  MapPin,
  Loader2,
  Hourglass,
  Mail,
  Phone,
  Headset,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/lib/supabase";
import { resolveEffectiveKeyPickup } from "@/hooks/v2/useProperties";
import { KEYNEST_DEFAULT_INSTRUCTIONS } from "@/lib/key-pickup";
import type { Apartment, Property, Room } from "@/types/property";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatAddress } from "@/lib/v2-format";
import {
  pickLiveTenantBooking,
  type LiveBookingCandidate,
} from "@/lib/tenant-live-booking";

// Support contact for Support us card
const SUPPORT_PHONE = "+49 89 54193354";
const SUPPORT_EMAIL = "kontakt@coliviq.de";

const DASH = "—";

type KeyNestDisplay = {
  accessUrl?: string | null;
  partnerShopName?: string | null;
  partnerShopAddress?: string | null;
  lockerNumber?: string | null;
  pickupCode?: string | null;
  accessCode?: string | null;
  openingHoursWeekdays?: string | null;
  openingHoursSaturday?: string | null;
  statusLabel?: string | null;
};

type BookingRoomKeynest = {
  keynest_access_url?: string | null;
  keynest_partner_shop_name?: string | null;
  keynest_partner_shop_address?: string | null;
  keynest_locker_number?: string | null;
  keynest_pickup_code?: string | null;
  keynest_access_code?: string | null;
  keynest_opening_hours_weekdays?: string | null;
  keynest_opening_hours_saturday?: string | null;
  keynest_status_label?: string | null;
  pickup_method?: string | null;
  pickup_instructions?: string | null;
  apartment_id?: number | null;
  apartments?: {
    keynest_pickup_location?: string | null;
    keynest_pickup_address?: string | null;
    pickup_method?: string | null;
    pickup_instructions?: string | null;
  } | null;
  properties?: {
    id?: string | number | null;
    title?: string | null;
    address?: string | null;
    zip_code?: string | null;
    city?: string | null;
    keynest_pickup_location?: string | null;
    keynest_pickup_address?: string | null;
    keynest_access_url?: string | null;
    pickup_method?: string | null;
    pickup_instructions?: string | null;
  } | null;
} | null;

// Fallback translations so German (and English) work when Storyblok cache is empty
const CHECKIN_FALLBACKS: Record<string, Record<string, string>> = {
  en: {
    "checkin.checkinCheckoutTitle": "Check-in & check-out",
    "checkin.subtitleLine1": "Everything you need to move in and move out.",
    "checkin.subtitleLine2": "Keys, steps, and support in one place.",
    "checkin.progressConfirmedHome": "Confirmed Home Details",
    "checkin.progressOnSchedule": "On Schedule for Start Date",
    "checkin.pickUpYourKeys": "Pick Up Your Keys",
    "checkin.stepByStep": "Your Check-In, Step by Step",
    "checkin.noConfirmedBooking":
      "You don't have a confirmed booking yet. Your check-in steps will appear here once your contract is confirmed.",
    "checkin.partnerShop": "Partner Shop",
    "checkin.lockerNumber": "Locker",
    "checkin.pickupCode": "Pickup Code",
    "checkin.yourAccessCode": "Your Access Code",
    "checkin.openInMaps": "Open in Maps",
    "checkin.presentCodeAtShop":
      "Present this code at the shop during opening hours",
    "checkin.openKeynestForCode":
      "Open KeyNest details to get your access code.",
    "checkin.pickupInstructions": "Pickup instructions",
    "checkin.keynestDefaultInstructions": KEYNEST_DEFAULT_INSTRUCTIONS.en,
    "checkin.openKeynestDetails": "Open KeyNest details",
    "checkin.keysLockedTitle": "Pick Up Your Keys",
    "checkin.keysUnlockOn":
      "Your key pickup details will be available from {date}.",
    "checkin.keysLockedHint":
      "Location and access code for keys will be shown the day before your move-in day.",
    "checkin.keyAccessAvailableIn": "Key access available in {countdown}",
    "checkin.keyAccessAvailableInLabel": "Key access available in",
    "checkin.unlocksAtBerlin": "Unlocks {datetime} · Berlin time",
    "checkin.keysAvailableNow": "Available now",
    "checkin.keysLockedChip": "Available soon",
    "checkin.pickupLocation": "Pickup location",
    "checkin.nextSteps": "Your next steps",
    "checkin.stepPickupKeys": "Pick up your keys at the partner shop",
    "checkin.stepArrive": "Head to your property",
    "checkin.stepSettleIn": "Settle in and enjoy your new home",
    "checkin.yourHomeDetails": "Your Home Details",
    "checkin.confirmed": "Confirmed",
    "checkin.yourStartDate": "Your Start Date",
    "checkin.keysAvailableFromDate": "Keys available from this date",
    "checkin.notSureHint": "Not sure? Find your contract in",
    "checkin.home": "Home",
    "checkin.myContract": "My Contract",
    "checkin.tabSummary": "Summary",
    "checkin.tabInfo": "Info",
    "checkin.checkinSummary": "Your Check-In Summary",
    "checkin.property": "Property",
    "checkin.startDate": "Move-in date",
    "checkin.moveOutDate": "Move-out date",
    "checkin.location": "Location",
    "checkin.keyPickup": "Key Pickup",
    "checkin.arrivalSent": "We've recorded your arrival. Welcome home!",
    "checkin.arrivalError": "Failed to send arrival notification",
    "checkin.supportTitle": "Support",
    "checkin.supportDesc":
      "Share your query with us. We respond within 2–3 business days with professional support.",
    "checkin.callUs": "Call us",
    "checkin.emailUs": "Email us",
  },
  de: {
    "checkin.checkinCheckoutTitle": "Einchecken & Auschecken",
    "checkin.subtitleLine1": "Alles für Ihren Ein- und Auszug.",
    "checkin.subtitleLine2": "Schlüssel, Schritte und Support an einem Ort.",
    "checkin.progressConfirmedHome": "Wohnung bestätigt",
    "checkin.progressOnSchedule": "Planmäßig zum Startdatum",
    "checkin.pickUpYourKeys": "Schlüssel abholen",
    "checkin.stepByStep": "Ihr Check-in, Schritt für Schritt",
    "checkin.noConfirmedBooking":
      "Sie haben noch keine bestätigte Buchung. Ihre Check-in-Schritte erscheinen hier, sobald Ihr Vertrag bestätigt ist.",
    "checkin.partnerShop": "Partner-Shop",
    "checkin.lockerNumber": "Fach",
    "checkin.pickupCode": "Abholcode",
    "checkin.yourAccessCode": "Ihr Zugangscode",
    "checkin.openInMaps": "In Karten öffnen",
    "checkin.presentCodeAtShop":
      "Zeigen Sie diesen Code während der Öffnungszeiten im Shop vor.",
    "checkin.openKeynestForCode":
      "Öffnen Sie die KeyNest-Details, um Ihren Zugangscode zu erhalten.",
    "checkin.pickupInstructions": "Abholhinweise",
    "checkin.keynestDefaultInstructions":
      "Sagen Sie dem Shop-Mitarbeiter, dass Sie einen KeyNest-Schlüssel abholen möchten, und nennen Sie ihm den Code.",
    "checkin.openKeynestDetails": "KeyNest-Details öffnen",
    "checkin.keysLockedTitle": "Schlüssel abholen",
    "checkin.keysUnlockOn":
      "Ihre Details zur Schlüsselabholung sind ab dem {date} verfügbar.",
    "checkin.keysLockedHint":
      "Standort und Zugangscode für die Schlüssel werden am Tag vor Ihrem Einzugstag angezeigt.",
    "checkin.keyAccessAvailableIn": "Schlüsselzugang verfügbar in {countdown}",
    "checkin.keyAccessAvailableInLabel": "Schlüsselzugang verfügbar in",
    "checkin.unlocksAtBerlin": "Freischaltung {datetime} · Berliner Zeit",
    "checkin.keysAvailableNow": "Jetzt verfügbar",
    "checkin.keysLockedChip": "Bald verfügbar",
    "checkin.pickupLocation": "Abholort",
    "checkin.nextSteps": "Ihre nächsten Schritte",
    "checkin.stepPickupKeys": "Schlüssel im Partner-Shop abholen",
    "checkin.stepArrive": "Zur Wohnung fahren",
    "checkin.stepSettleIn": "Einrichten und Ihr neues Zuhause genießen",
    "checkin.yourHomeDetails": "Ihre Wohnung",
    "checkin.confirmed": "Bestätigt",
    "checkin.yourStartDate": "Ihr Startdatum",
    "checkin.keysAvailableFromDate": "Schlüssel ab diesem Datum verfügbar",
    "checkin.notSureHint": "Unsicher? Vertrag unter",
    "checkin.home": "Start",
    "checkin.myContract": "Mein Vertrag",
    "checkin.tabSummary": "Zusammenfassung",
    "checkin.tabInfo": "Info",
    "checkin.checkinSummary": "Ihre Check-in-Zusammenfassung",
    "checkin.property": "Objekt",
    "checkin.startDate": "Einzugsdatum",
    "checkin.moveOutDate": "Auszugsdatum",
    "checkin.location": "Standort",
    "checkin.keyPickup": "Schlüsselabholung",
    "checkin.arrivalSent": "Ankunft erfasst. Willkommen zu Hause!",
    "checkin.arrivalError": "Ankunftsmeldung konnte nicht gesendet werden",
    "checkin.supportTitle": "Kundenservice",
    "checkin.supportDesc":
      "Senden Sie uns Ihre Anfrage. Wir antworten innerhalb von 2–3 Werktagen professionell.",
    "checkin.callUs": "Anrufen",
    "checkin.emailUs": "E-Mail",
  },
};

function formatDate(dateStr: string, locale: string = "en-GB") {
  const d = new Date(dateStr);
  return d.toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// KeyNest pickup details unlock this many days before move-in.
const KEYNEST_UNLOCK_DAYS_BEFORE = 1;

// Pickup details unlock at this wall-clock time (00:01) on the unlock day,
// judged in the property's timezone — see PROPERTY_TIME_ZONE.
const KEYNEST_UNLOCK_HOUR = 0;
const KEYNEST_UNLOCK_MINUTE = 1;

// Properties are in Germany, so the unlock instant is fixed to Berlin local
// time (CET/CEST) regardless of where the tenant's device is.
const PROPERTY_TIME_ZONE = "Europe/Berlin";

// Parse a calendar date ("YYYY-MM-DD" or a full ISO timestamp) into a Date at
// LOCAL midnight. We deliberately ignore any time/timezone suffix so the unlock
// is judged on the calendar day, not a UTC instant — this avoids the card
// flickering on/off around midnight for tenants in different timezones.
function parseLocalDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
  if (!m) return null;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

// Offset (ms) of `timeZone` from UTC at the given instant. Positive east of UTC.
function tzOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const map: Record<string, number> = {};
  for (const p of parts) {
    if (p.type !== "literal") map[p.type] = Number(p.value);
  }
  const asUTC = Date.UTC(
    map.year,
    map.month - 1,
    map.day,
    map.hour,
    map.minute,
    map.second
  );
  return asUTC - instant.getTime();
}

// Resolve a wall-clock time in `timeZone` to the matching absolute instant.
// (DST is handled via the offset at the candidate instant; the unlock time of
// 00:01 never lands on a Berlin DST transition, which happen at 02:00/03:00.)
function zonedWallTimeToInstant(
  year: number,
  monthIndex: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string
): Date {
  const naiveUTC = Date.UTC(year, monthIndex, day, hour, minute);
  const offset = tzOffsetMs(new Date(naiveUTC), timeZone);
  return new Date(naiveUTC - offset);
}

// Format a remaining duration (ms) as a compact countdown, e.g. "2d 05:09:13"
// or "05:09:13" when under a day.
function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  const clock = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return days > 0 ? `${days}d ${clock}` : clock;
}

export default function CheckInCheckOut() {
  const { user } = useAuth();
  const { t, language } = useLanguage();

  const getCheckinText = (key: string): string => {
    const fromFallback =
      CHECKIN_FALLBACKS[language]?.[key] ?? CHECKIN_FALLBACKS.en[key];
    if (fromFallback && fromFallback.trim()) return fromFallback;
    const fromCache = t(key);
    if (fromCache && fromCache.trim()) return fromCache;
    return fromFallback ?? "";
  };
  const [arrived, setArrived] = useState(false);
  const [arrivalSending, setArrivalSending] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("name, email")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: approvedBookings = [] } = useQuery({
    // Distinct from Dashboard's ["tenant-approved-bookings", user?.id] query,
    // which selects fewer columns. Sharing the exact key let Dashboard's leaner
    // result win the cache, so this component never received the KeyNest/pickup
    // fields and key pickup rendered blank. The extra segment gives us our own
    // cache entry while staying covered by the existing prefix invalidations.
    queryKey: ["tenant-approved-bookings", user?.id, "keynest"],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          id,
          start_date,
          end_date,
          move_in_date,
          move_out_date,
          room_number,
          status,
          contract_signed_at,
          rooms (
            id,
            title,
            property_id,
            apartment_id,
            keynest_access_url,
            keynest_partner_shop_name,
            keynest_partner_shop_address,
            keynest_locker_number,
            keynest_pickup_code,
            keynest_access_code,
            keynest_opening_hours_weekdays,
            keynest_opening_hours_saturday,
            keynest_status_label,
            pickup_method,
            pickup_instructions,
            apartments (
              keynest_pickup_location,
              keynest_pickup_address,
              pickup_method,
              pickup_instructions
            ),
            properties (
              id,
              title,
              address,
              city,
              zip_code,
              keynest_pickup_location,
              keynest_pickup_address,
              keynest_access_url,
              pickup_method,
              pickup_instructions
            )
          )
        `
        )
        .eq("user_id", user.id)
        .is("admin_archived_at", null)
        .eq("status", "confirmed")
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Ranked by the shared resolver, not "first row wins", so key pickup and the
  // protocol page always describe the same booking.
  const activeBooking = useMemo(
    () => pickLiveTenantBooking(approvedBookings as LiveBookingCandidate[]),
    [approvedBookings]
  ) as (typeof approvedBookings)[number] | null;

  const property = (activeBooking?.rooms as BookingRoomKeynest)?.properties;
  const fullAddress = property?.address
    ? formatAddress(property.address, property.zip_code, property.city)
    : null;
  const propertyTitle = property?.title || fullAddress || "—";

  const keynest: KeyNestDisplay = useMemo(() => {
    const room = (activeBooking?.rooms as BookingRoomKeynest) ?? null;
    if (!room) return {};
    return {
      // Per-room link wins; fall back to the property-level link for
      // single-key properties.
      accessUrl:
        room.keynest_access_url ?? room.properties?.keynest_access_url ?? null,
      partnerShopName: room.keynest_partner_shop_name ?? null,
      partnerShopAddress: room.keynest_partner_shop_address ?? null,
      lockerNumber: room.keynest_locker_number ?? null,
      pickupCode: room.keynest_pickup_code ?? null,
      accessCode: room.keynest_access_code ?? null,
      openingHoursWeekdays: room.keynest_opening_hours_weekdays ?? null,
      openingHoursSaturday: room.keynest_opening_hours_saturday ?? null,
      statusLabel: room.keynest_status_label ?? null,
    };
  }, [activeBooking]);

  // Effective pickup LOCATION, resolved across the inheritance chain
  // room override → apartment override → property default. The room override
  // reuses the legacy keynest_partner_shop_* columns. The KeyNest access link
  // itself is per-room (keynest.accessUrl) and is never inherited.
  const effectivePickup = useMemo(() => {
    const room = (activeBooking?.rooms as BookingRoomKeynest) ?? null;
    return resolveEffectiveKeyPickup({
      room: room as unknown as Room | null,
      apartment: (room?.apartments ?? null) as unknown as Apartment | null,
      property: (room?.properties ?? null) as unknown as Property | null,
    });
  }, [activeBooking]);

  // The card is shown when there's something to pick up: a KeyNest link or a
  // resolved pickup location/address from any level.
  // KeyNest pickups with nothing written get the default, in the tenant's
  // language; custom text is shown as the admin wrote it.
  const instructionsText =
    effectivePickup.instructions ||
    (effectivePickup.method === "keynest"
      ? getCheckinText("checkin.keynestDefaultInstructions")
      : null);

  const keynestHasAny = useMemo(() => {
    return (
      !!keynest.accessUrl ||
      !!effectivePickup.location ||
      !!effectivePickup.address ||
      !!effectivePickup.instructions
    );
  }, [keynest, effectivePickup]);

  // Gate KeyNest details by move-in date: they unlock at 00:01 (Berlin time)
  // KEYNEST_UNLOCK_DAYS_BEFORE days before this tenant's move-in (per booking).
  // Returns the exact unlock instant so we can show a live countdown to it.
  // Null = no usable date, so we fail open and never hide data we can't gate.
  const keynestUnlockAt = useMemo(() => {
    const moveIn = parseLocalDate(
      activeBooking?.move_in_date || activeBooking?.start_date
    );
    if (!moveIn) return null;
    const unlockDay = new Date(moveIn);
    unlockDay.setDate(unlockDay.getDate() - KEYNEST_UNLOCK_DAYS_BEFORE);
    return zonedWallTimeToInstant(
      unlockDay.getFullYear(),
      unlockDay.getMonth(),
      unlockDay.getDate(),
      KEYNEST_UNLOCK_HOUR,
      KEYNEST_UNLOCK_MINUTE,
      PROPERTY_TIME_ZONE
    );
  }, [activeBooking]);

  // Tick once a second so the countdown updates and the gate flips live at 00:01
  // without a page reload. Only runs while the keys are still locked.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const keynestUnlocked =
    !keynestUnlockAt || nowMs >= keynestUnlockAt.getTime();
  useEffect(() => {
    if (keynestUnlocked) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [keynestUnlocked]);

  const keynestCountdown = keynestUnlockAt
    ? formatCountdown(keynestUnlockAt.getTime() - nowMs)
    : "";
  // The unlock instant rendered in the property's timezone, so the tenant sees
  // exactly when keys unlock in Berlin time regardless of their device clock.
  const keynestUnlockBerlinLabel = keynestUnlockAt
    ? keynestUnlockAt.toLocaleString(language === "de" ? "de-DE" : "en-GB", {
        timeZone: PROPERTY_TIME_ZONE,
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const sendArrivalNotification = async () => {
    if (!user) return;
    setArrivalSending(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "send-checkin-notification",
        {
          body: { type: "arrival" },
        }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setArrived(true);
      toast.success(getCheckinText("checkin.arrivalSent"));
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : undefined;
      toast.error(message || getCheckinText("checkin.arrivalError"));
    } finally {
      setArrivalSending(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-6">
      <h2 className="text-xl sm:text-2xl font-bold text-foreground">
        {getCheckinText("checkin.checkinCheckoutTitle")}
      </h2>
      <p className="text-sm text-muted-foreground">
        {getCheckinText("checkin.subtitleLine1")}
        <br />
        {getCheckinText("checkin.subtitleLine2")}
      </p>
      {/* Progress tracker – horizontal with connectors */}
      <div>
        <div className="flex items-center border-border/60">
          <div className="flex items-center flex-1 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <CheckCircle2 className="h-4 w-4" />
            </div>
            <div
              className="h-px flex-1 min-w-[12px] max-w-[40px] bg-border mx-1 sm:mx-2"
              aria-hidden
            />
            <span className="text-xs sm:text-sm font-medium text-foreground truncate">
              {getCheckinText("checkin.progressConfirmedHome")}
            </span>
          </div>
          <div className="flex items-center flex-1 min-w-0">
            <div className="h-px w-2 sm:w-4 bg-border shrink-0" aria-hidden />
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <CheckCircle2 className="h-4 w-4" />
            </div>
            <div
              className="h-px flex-1 min-w-[12px] max-w-[40px] bg-border mx-1 sm:mx-2"
              aria-hidden
            />
            <span className="text-xs sm:text-sm font-medium text-foreground truncate">
              {getCheckinText("checkin.progressOnSchedule")}
            </span>
          </div>
          <div className="flex items-center flex-1 min-w-0">
            <div className="h-px w-2 sm:w-4 bg-border shrink-0" aria-hidden />
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted border-2 border-border">
              <span className="h-2 w-2 rounded-full bg-muted-foreground" />
            </div>
            <div
              className="h-px flex-1 min-w-[12px] max-w-[40px] bg-border mx-1 sm:mx-2"
              aria-hidden
            />
            <span className="text-xs sm:text-sm font-medium text-muted-foreground truncate">
              {getCheckinText("checkin.pickUpYourKeys")}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left + center: steps */}
        <div className="lg:col-span-2 space-y-4">
          <h3 className="text-base font-semibold text-foreground">
            {getCheckinText("checkin.stepByStep")}
          </h3>

          {!activeBooking ? (
            <div className="space-y-4">
              <Card>
                <CardContent className="py-6 text-center text-muted-foreground">
                  <p className="text-sm">
                    {getCheckinText("checkin.noConfirmedBooking")}
                  </p>
                </CardContent>
              </Card>
              {/* Next steps */}
              <div className="mt-4">
                <Card className="rounded-2xl border border-border/60 bg-white dark:bg-background shadow-sm overflow-hidden">
                  <CardContent className="pt-5 pb-5 px-5">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Key className="h-5 w-5" />
                      </div>
                      <CardTitle className="text-base font-bold m-0 text-foreground">
                        {getCheckinText("checkin.nextSteps")}
                      </CardTitle>
                    </div>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-3 text-sm">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium">
                          1
                        </span>
                        <span className="text-muted-foreground">
                          {getCheckinText("checkin.stepPickupKeys")}
                        </span>
                      </li>
                      <li className="flex items-center gap-3 text-sm">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium">
                          2
                        </span>
                        <span className="text-muted-foreground">
                          {getCheckinText("checkin.stepArrive")}
                        </span>
                      </li>
                      <li className="flex items-center gap-3 text-sm">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium">
                          3
                        </span>
                        <span className="text-muted-foreground">
                          {getCheckinText("checkin.stepSettleIn")}
                        </span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Your Home Details – white card, no background color (match reference) */}
              <Card className="rounded-2xl border border-border/60 bg-white dark:bg-background shadow-sm overflow-hidden">
                <CardContent className="pt-5 pb-5 px-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-foreground shrink-0" />
                      <span className="font-bold text-foreground">
                        {getCheckinText("checkin.yourHomeDetails")}
                      </span>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                  </div>
                  <p className="text-sm text-foreground mt-3">
                    {fullAddress || "—"}
                  </p>
                  <Link
                    to="/dashboard"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1 mt-2 font-medium"
                  >
                    {getCheckinText("checkin.confirmed")}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </CardContent>
              </Card>

              {/* Your Start Date – white card, no background color (match reference) */}
              <Card className="rounded-2xl border border-border/60 bg-white dark:bg-background shadow-sm overflow-hidden">
                <CardContent className="pt-5 pb-5 px-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-foreground shrink-0" />
                      <span className="font-bold text-foreground">
                        {getCheckinText("checkin.yourStartDate")}
                      </span>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                  </div>
                  <p className="text-sm font-medium text-foreground mt-3">
                    {formatDate(activeBooking.start_date, language)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {getCheckinText("checkin.keysAvailableFromDate")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-3">
                    {getCheckinText("checkin.notSureHint")}{" "}
                    <Link
                      to="/dashboard"
                      className="text-primary hover:underline"
                    >
                      {getCheckinText("checkin.home")}
                    </Link>
                    {" → "}
                    <Link
                      to="/dashboard?tab=contract"
                      className="text-primary hover:underline"
                    >
                      {getCheckinText("checkin.myContract")}
                    </Link>
                  </p>
                </CardContent>
              </Card>

              {/* Pick Up Your Keys – gated to unlock at 00:01 the day before
                  move-in. Clean monochrome design: a countdown hero while
                  locked, pickup location + actions once unlocked. */}
              {keynestHasAny ? (
                <Card className="md:col-span-2 rounded-2xl border border-border/60 bg-white dark:bg-background shadow-sm overflow-hidden">
                  <CardHeader className="pb-3 pt-5 px-6 border-b border-border/60">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted">
                          <Key className="h-4 w-4 text-foreground" />
                        </div>
                        <CardTitle className="text-base font-semibold m-0">
                          {getCheckinText("checkin.pickUpYourKeys")}
                        </CardTitle>
                      </div>
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2.5 py-1 text-xs font-medium text-muted-foreground">
                        {keynestUnlocked ? (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {getCheckinText("checkin.keysAvailableNow")}
                          </>
                        ) : (
                          <>
                            <Hourglass className="h-3.5 w-3.5" />
                            {getCheckinText("checkin.keysLockedChip")}
                          </>
                        )}
                      </span>
                    </div>
                  </CardHeader>

                  <CardContent className="p-6">
                    {keynestUnlocked ? (
                      <div className="space-y-6">
                        <div>
                          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
                            {getCheckinText("checkin.pickupLocation")}
                          </p>
                          <p className="text-lg font-semibold text-foreground leading-tight">
                            {effectivePickup.location || DASH}
                          </p>
                          {effectivePickup.address ? (
                            <p className="mt-1.5 flex items-start gap-1.5 text-sm text-muted-foreground">
                              <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                              <span>{effectivePickup.address}</span>
                            </p>
                          ) : null}
                        </div>

                        {instructionsText ? (
                          <div>
                            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
                              {getCheckinText("checkin.pickupInstructions")}
                            </p>
                            <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-line">
                              {instructionsText}
                            </p>
                          </div>
                        ) : null}

                        <div className="flex flex-wrap gap-2.5">
                          {effectivePickup.method === "keynest" &&
                          keynest.accessUrl ? (
                            <Button
                              asChild
                              className="rounded-xl bg-foreground text-background hover:bg-foreground/90"
                            >
                              <a
                                href={keynest.accessUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2"
                              >
                                {getCheckinText("checkin.openKeynestDetails")}
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                          ) : null}
                          {effectivePickup.address ? (
                            <Button
                              asChild
                              variant="outline"
                              className="rounded-xl"
                            >
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(effectivePickup.address)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2"
                              >
                                <MapPin className="h-3.5 w-3.5" />
                                {getCheckinText("checkin.openInMaps")}
                              </a>
                            </Button>
                          ) : null}
                        </div>

                        {effectivePickup.method === "keynest" &&
                        keynest.accessUrl ? (
                          <p className="text-xs text-muted-foreground">
                            {getCheckinText("checkin.openKeynestForCode")}
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <div className="space-y-5">
                        <div className="rounded-2xl border border-border/70 bg-muted/40 px-6 py-8 text-center">
                          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                            {getCheckinText(
                              "checkin.keyAccessAvailableInLabel"
                            )}
                          </p>
                          <p className="mt-2.5 font-semibold tabular-nums tracking-tight text-foreground text-4xl sm:text-5xl">
                            {keynestCountdown}
                          </p>
                          {keynestUnlockBerlinLabel ? (
                            <p className="mt-3 text-xs text-muted-foreground">
                              {getCheckinText(
                                "checkin.unlocksAtBerlin"
                              ).replace("{datetime}", keynestUnlockBerlinLabel)}
                            </p>
                          ) : null}
                        </div>
                        <p className="mx-auto max-w-md text-center text-sm text-muted-foreground">
                          {getCheckinText("checkin.keysLockedHint")}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : null}

              {/* Next steps */}
              <Card className="rounded-2xl border border-border/60 bg-white dark:bg-background shadow-sm overflow-hidden">
                <CardContent className="pt-5 pb-5 px-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Key className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-base font-bold m-0 text-foreground">
                      {getCheckinText("checkin.nextSteps")}
                    </CardTitle>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-sm">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium">
                        1
                      </span>
                      <span className="text-muted-foreground">
                        {getCheckinText("checkin.stepPickupKeys")}
                      </span>
                    </li>
                    <li className="flex items-center gap-3 text-sm">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium">
                        2
                      </span>
                      <span className="text-muted-foreground">
                        {getCheckinText("checkin.stepArrive")}
                      </span>
                    </li>
                    <li className="flex items-center gap-3 text-sm">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium">
                        3
                      </span>
                      <span className="text-muted-foreground">
                        {getCheckinText("checkin.stepSettleIn")}
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Right column: tabs – Summary | Info */}
        <div className="space-y-4">
          <Tabs defaultValue="summary" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-11 rounded-xl bg-muted/50 p-1">
              <TabsTrigger
                value="summary"
                className="rounded-lg text-sm font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                {getCheckinText("checkin.tabSummary")}
              </TabsTrigger>
              <TabsTrigger
                value="info"
                className="rounded-lg text-sm font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                {getCheckinText("checkin.tabInfo")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="mt-4 space-y-4">
              {/* Your Check-In Summary – match reference (Property, Start Date, Key Pickup, status pill) */}
              <Card className="rounded-2xl border shadow-sm overflow-hidden">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-base font-bold text-foreground">
                    {getCheckinText("checkin.checkinSummary")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-500/15 text-sky-700 dark:text-sky-300">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {getCheckinText("checkin.property")}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-foreground rounded-lg border border-sky-500/25 bg-sky-500/10 px-3 py-2">
                          {activeBooking ? propertyTitle || fullAddress : DASH}
                        </p>
                      </div>
                      {activeBooking && fullAddress && property?.title ? (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            {getCheckinText("checkin.location")}
                          </p>
                          <p className="mt-1 inline-flex items-start gap-2 text-sm font-medium text-sky-900 dark:text-sky-100 rounded-lg border border-sky-500/30 bg-sky-500/12 px-3 py-2 max-w-full">
                            <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-sky-600 dark:text-sky-400" />
                            <span className="break-words">{fullAddress}</span>
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {getCheckinText("checkin.startDate")}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-primary w-fit rounded-lg border border-primary/25 bg-primary/10 px-3 py-2 tabular-nums">
                        {activeBooking &&
                        (activeBooking.move_in_date || activeBooking.start_date)
                          ? formatDate(
                              activeBooking.move_in_date ||
                                activeBooking.start_date,
                              language
                            )
                          : DASH}
                      </p>
                    </div>
                  </div>
                  {activeBooking &&
                  (activeBooking.move_out_date || activeBooking.end_date) ? (
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-700 dark:text-violet-300">
                        <Calendar className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {getCheckinText("checkin.moveOutDate")}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-violet-900 dark:text-violet-100 w-fit rounded-lg border border-violet-500/25 bg-violet-500/12 px-3 py-2 tabular-nums">
                          {formatDate(
                            activeBooking.move_out_date ||
                              activeBooking.end_date,
                            language
                          )}
                        </p>
                      </div>
                    </div>
                  ) : null}
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted">
                      <Key className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {getCheckinText("checkin.keyPickup")}
                      </p>
                      <p className="text-sm font-semibold text-foreground">
                        {activeBooking && keynestUnlocked
                          ? effectivePickup.location || DASH
                          : DASH}
                      </p>
                    </div>
                  </div>
                  {activeBooking && keynestHasAny && !keynestUnlocked ? (
                    <div className="rounded-xl border border-border/70 bg-muted/40 px-4 py-3">
                      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        <Hourglass className="h-3.5 w-3.5 shrink-0" />
                        {getCheckinText("checkin.keyAccessAvailableInLabel")}
                      </p>
                      <p className="mt-1 text-xl font-semibold tabular-nums tracking-tight text-foreground">
                        {keynestCountdown}
                      </p>
                      {keynestUnlockBerlinLabel ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {getCheckinText("checkin.unlocksAtBerlin").replace(
                            "{datetime}",
                            keynestUnlockBerlinLabel
                          )}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {/* Support – not "Tenant Support"; professional query text */}
              <Card className="rounded-2xl border shadow-sm overflow-hidden">
                <CardContent className="pt-4 pb-4 px-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Headset className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-semibold text-foreground">
                        {getCheckinText("checkin.supportTitle")}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {getCheckinText("checkin.supportDesc")}
                      </p>
                      <div className="mt-3 space-y-2">
                        <a
                          href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}
                          className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-xs font-medium text-foreground hover:bg-muted/60 transition-colors"
                        >
                          <Phone className="h-3.5 w-3.5 shrink-0 text-primary" />
                          <span className="text-muted-foreground">
                            {getCheckinText("checkin.callUs")}:
                          </span>
                          <span className="font-semibold tabular-nums ml-auto">
                            {SUPPORT_PHONE}
                          </span>
                        </a>
                        <a
                          href={`mailto:${SUPPORT_EMAIL}`}
                          className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-xs font-medium text-foreground hover:bg-muted/60 transition-colors"
                        >
                          <Mail className="h-3.5 w-3.5 shrink-0 text-primary" />
                          <span className="text-muted-foreground">
                            {getCheckinText("checkin.emailUs")}:
                          </span>
                          <span className="font-semibold ml-auto truncate">
                            {SUPPORT_EMAIL}
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="info" className="mt-4 space-y-4">
              <Card className="rounded-2xl border shadow-sm overflow-hidden">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-base font-bold flex items-center gap-2">
                    {getCheckinText("checkin.yourAccessCode")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  {/* The raw access code is never shown inline — the tenant
                      retrieves it from KeyNest. Gated to the same 00:01
                      (day before move-in) unlock as the rest of key pickup. */}
                  {activeBooking && keynestUnlocked ? (
                    effectivePickup.method === "manual" &&
                    keynest.accessCode ? (
                      <p className="text-center font-mono text-lg font-semibold py-2">
                        {keynest.accessCode}
                      </p>
                    ) : keynest.accessUrl ? (
                      <div className="space-y-3 text-center">
                        <p className="text-sm text-muted-foreground">
                          {getCheckinText("checkin.openKeynestForCode")}
                        </p>
                        <div className="flex justify-center">
                          <Button
                            variant="secondary"
                            className="rounded-xl"
                            asChild
                          >
                            <a
                              href={keynest.accessUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2"
                            >
                              {getCheckinText("checkin.openKeynestDetails")}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-sm text-muted-foreground py-2">
                        {DASH}
                      </p>
                    )
                  ) : (
                    <p className="text-center text-sm text-muted-foreground py-2">
                      {getCheckinText("checkin.keysLockedHint")}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
